/** A coach's own notes about their athletes (DESIGN §16.8): for the author only. */

export interface Note {
  id: string;
  athleteID: string;
  body: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export const NOTE_MAX = 1000;

export function sortNotes(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

/** An athlete's notes as their page lists them: the pinned one first, then newest first. */
export function notesFor(notes: Note[], athleteID: string): Note[] {
  const theirs = sortNotes(notes.filter((n) => n.athleteID === athleteID));
  return [...theirs.filter((n) => n.pinned), ...theirs.filter((n) => !n.pinned)];
}

export function pinnedFor(notes: Note[] | undefined, athleteID: string): Note | undefined {
  return notes?.find((n) => n.athleteID === athleteID && n.pinned);
}

export function hasNotes(notes: Note[] | undefined, athleteID: string): boolean {
  return !!notes?.some((n) => n.athleteID === athleteID);
}

/** What the list holds once the server has answered a write; pinning one unpins the athlete's other, as the server does. */
export function applyNote(notes: Note[], note: Note): Note[] {
  const others = notes.filter((n) => n.id !== note.id).map((n) => (note.pinned && n.pinned && n.athleteID === note.athleteID ? { ...n, pinned: false } : n));
  return sortNotes([note, ...others]);
}

export function removeNote(notes: Note[], id: string): Note[] {
  return notes.filter((n) => n.id !== id);
}

export function oneLine(body: string): string {
  return body.replace(/\s+/g, " ").trim();
}

function day(iso: string, thisYear: number): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const text = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return d.getFullYear() === thisYear ? text : text + ", " + d.getFullYear();
}

export function edited(note: Note): boolean {
  return new Date(note.updatedAt).getTime() > new Date(note.createdAt).getTime();
}

/** "Oct 14", or "Oct 14 · edited Oct 15" once it has changed; the year only when it isn't this one. */
export function noteDate(note: Note, thisYear = new Date().getFullYear()): string {
  const made = day(note.createdAt, thisYear);
  return edited(note) ? made + " · edited " + day(note.updatedAt, thisYear) : made;
}

export interface NoteItem {
  id: string;
  label: string;
  sub: string;
  athleteID: string;
}

/** ⌘K: a note per item, "Note · Dana Lee" with its words, for athletes still on the team. */
export function noteItems(notes: Note[] | undefined, athletes: { id: string; name: string }[]): NoteItem[] {
  if (!notes) return [];
  const names = new Map(athletes.map((a) => [a.id, a.name]));
  return sortNotes(notes)
    .filter((n) => names.has(n.athleteID))
    .map((n) => ({ id: "note:" + n.id, label: "Note · " + names.get(n.athleteID)!, sub: oneLine(n.body), athleteID: n.athleteID }));
}
